/**
 * TextureCompression.js - Advanced Texture Optimization and Compression System
 * 
 * Features:
 * - Hardware-accelerated texture compression (WebGL extensions)
 * - Texture atlases generation and management
 * - Automatic mipmap generation
 * - Texture streaming with async loading
 * - GPU memory optimization
 * - Automatic format selection based on device capabilities
 */

class TextureCompression {
    constructor(gl) {
        this.gl = gl;
        this.capabilities = this._detectCapabilities();
        this.compressedFormats = this._detectCompressedFormats();
        this.textureCache = new Map();
        this.atlasManager = new TextureAtlasManager(gl);
        this.streamingManager = new TextureStreamingManager(gl);
        this.memoryManager = new GPUMemoryManager(gl);
        this.compressionWorker = null;
        
        this._initWorker();
    }

    /**
     * Detect WebGL capabilities and extensions
     */
    _detectCapabilities() {
        const gl = this.gl;
        const isWebGL2 = gl instanceof WebGL2RenderingContext;
        
        return {
            isWebGL2,
            maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
            maxCubeMapSize: gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE),
            maxArrayTextureLayers: isWebGL2 ? gl.getParameter(gl.MAX_ARRAY_TEXTURE_LAYERS) : 0,
            supportBgraExtension: !!gl.getExtension('EXT_texture_format_BGRA8888'),
            supportDepthTexture: !!gl.getExtension('WEBGL_depth_texture'),
            supportFloatTextures: !!gl.getExtension('OES_texture_float'),
            supportHalfFloatTextures: !!gl.getExtension('OES_texture_half_float'),
            supportS3TC: !!gl.getExtension('WEBGL_compressed_texture_s3tc'),
            supportASTC: !!gl.getExtension('WEBGL_compressed_texture_astc'),
            supportETC1: !!gl.getExtension('WEBGL_compressed_texture_etc1'),
            supportETC2: isWebGL2 && !!gl.getExtension('WEBGL_compressed_texture_etc'),
            supportPVRT: !!gl.getExtension('WEBGL_compressed_texture_pvrtc'),
            supportATC: !!gl.getExtension('WEBGL_compressed_texture_atc'),
            supportBC: isWebGL2 && !!gl.getExtension('EXT_texture_compression_bptc'),
            supportRGTC: isWebGL2 && !!gl.getExtension('EXT_texture_compression_rgtc'),
            supportLossless: !!gl.getExtension('EXT_lossless_etc_s3tc'),
            supportAnisotropicFiltering: !!gl.getExtension('EXT_texture_filter_anisotropic'),
            maxAnisotropy: gl.getParameter(gl.getExtension('EXT_texture_filter_anisotropic')?.MAX_TEXTURE_MAX_ANISOTROPY_EXT || 0)
        };
    }

    /**
     * Detect available compressed texture formats
     */
    _detectCompressedFormats() {
        const formats = {
            available: [],
            fallback: []
        };
        
        const addFormat = (format, internalFormat, extension, priority = 0) => {
            if (this.capabilities[extension]) {
                formats.available.push({
                    format,
                    internalFormat,
                    extension,
                    priority,
                    name: format.toUpperCase()
                });
            }
        };

        // High priority formats (mobile-first)
        addFormat('COMPRESSED_RGB_ETC1', null, 'supportETC1', 95);
        addFormat('COMPRESSED_RGBA_ASTC', null, 'supportASTC', 90);
        addFormat('COMPRESSED_RGB_PVRTC', null, 'supportPVRT', 85);
        
        // Desktop formats
        addFormat('COMPRESSED_RGB_S3TC', null, 'supportS3TC', 80);
        addFormat('COMPRESSED_RGBA_S3TC', null, 'supportS3TC', 75);
        
        // WebGL2 formats
        addFormat('COMPRESSED_RGB_ETC2', null, 'supportETC2', 85);
        addFormat('COMPRESSED_RGBA_ETC2', null, 'supportETC2', 80);
        
        // Sort by priority
        formats.available.sort((a, b) => b.priority - a.priority);
        
        // Fallback formats
        formats.fallback = [
            gl.RGBA8,
            gl.RGBA,
            gl.RGB8,
            gl.RGB,
            gl.LUMINANCE_ALPHA,
            gl.LUMINANCE
        ];
        
        return formats;
    }

    /**
     * Initialize compression worker for background processing
     */
    _initWorker() {
        try {
            const workerCode = `
                self.onmessage = function(e) {
                    const { type, data } = e.data;
                    
                    switch(type) {
                        case 'compressTexture':
                            self.compressTexture(data);
                            break;
                        case 'generateMipmaps':
                            self.generateMipmaps(data);
                            break;
                        case 'createAtlas':
                            self.createAtlas(data);
                            break;
                    }
                };

                function compressTexture(data) {
                    const { imageData, compressionType, quality } = data;
                    // Simulate compression (in real implementation, use WASM libraries)
                    const compressedData = imageData; // Placeholder
                    self.postMessage({ type: 'textureCompressed', data: compressedData });
                }

                function generateMipmaps(data) {
                    const { imageData, levels } = data;
                    // Simulate mipmap generation
                    const mipmaps = [];
                    for (let i = 0; i < levels; i++) {
                        mipmaps.push(imageData); // Placeholder
                    }
                    self.postMessage({ type: 'mipmapsGenerated', data: mipmaps });
                }

                function createAtlas(data) {
                    const { textures, atlasSize } = data;
                    // Simulate atlas creation
                    const atlas = { canvas: document.createElement('canvas'), mappings: {} };
                    self.postMessage({ type: 'atlasCreated', data: atlas });
                }
            `;
            
            const blob = new Blob([workerCode], { type: 'application/javascript' });
            this.compressionWorker = new Worker(URL.createObjectURL(blob));
            
            this.compressionWorker.onmessage = (e) => {
                this._handleWorkerMessage(e.data);
            };
        } catch (error) {
            console.warn('Compression worker initialization failed:', error);
        }
    }

    /**
     * Handle worker messages
     */
    _handleWorkerMessage(data) {
        // Handle compression results
        if (data.type === 'textureCompressed') {
            // Process compressed texture
        } else if (data.type === 'mipmapsGenerated') {
            // Process mipmaps
        } else if (data.type === 'atlasCreated') {
            // Process atlas
        }
    }

    /**
     * Select optimal texture format based on device capabilities and texture type
     */
    selectOptimalFormat(textureType = 'diffuse', alpha = false, quality = 'medium') {
        // Use compressed formats if available
        if (this.compressedFormats.available.length > 0) {
            for (const format of this.compressedFormats.available) {
                // Check if format supports alpha if needed
                if (!alpha || format.format.includes('RGBA') || format.format.includes('ETC2')) {
                    return format;
                }
            }
        }
        
        // Fallback to uncompressed formats
        const preferred = alpha ? this.gl.RGBA : this.gl.RGB;
        const qualityMap = {
            'low': this.gl.RGB5_A1,
            'medium': this.gl.RGBA8,
            'high': this.capabilities.supportHalfFloatTextures ? this.gl.RGBA16F : this.gl.RGBA16
        };
        
        return {
            format: preferred,
            internalFormat: qualityMap[quality] || this.gl.RGBA8,
            name: 'FALLBACK'
        };
    }

    /**
     * Compress texture using hardware acceleration
     */
    async compressTexture(imageData, options = {}) {
        const {
            compressionType = 'automatic',
            quality = 'medium',
            generateMipmaps = true,
            anisotropicFiltering = true,
            maxAnisotropy = 8
        } = options;

        return new Promise((resolve, reject) => {
            try {
                // Select optimal format
                const format = this.selectOptimalFormat(options.textureType, options.alpha, quality);
                
                // Create texture
                const texture = this.gl.createTexture();
                this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
                
                // Set parameters based on format
                this._setTextureParameters(texture, format, generateMipmaps, anisotropicFiltering, maxAnisotropy);
                
                // Upload texture data
                this.gl.texImage2D(
                    this.gl.TEXTURE_2D, 0,
                    format.internalFormat || this.gl.RGBA,
                    this.gl.RGBA, this.gl.UNSIGNED_BYTE,
                    imageData
                );
                
                // Generate mipmaps if requested
                if (generateMipmaps) {
                    this.gl.generateMipmap(this.gl.TEXTURE_2D);
                }
                
                // Store in cache
                const cacheKey = this._generateCacheKey(imageData, options);
                this.textureCache.set(cacheKey, {
                    texture,
                    format,
                    metadata: {
                        width: imageData.width || imageData.videoWidth,
                        height: imageData.height || imageData.videoHeight,
                        channels: 4,
                        compressed: !!format.internalFormat && format.internalFormat !== this.gl.RGBA8
                    }
                });
                
                this.gl.bindTexture(this.gl.TEXTURE_2D, null);
                
                resolve({
                    texture,
                    format: format.name,
                    compressed: !!format.internalFormat && format.internalFormat !== this.gl.RGBA8,
                    cacheKey
                });
                
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Set texture parameters based on format and options
     */
    _setTextureParameters(texture, format, generateMipmaps, anisotropicFiltering, maxAnisotropy) {
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, texture);
        
        // Wrapping
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        
        // Filtering
        if (generateMipmaps && this.capabilities.maxMipmapLevel) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        } else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        }
        
        // Anisotropic filtering
        if (anisotropicFiltering && this.capabilities.maxAnisotropy > 1) {
            const anisotropy = Math.min(maxAnisotropy, this.capabilities.maxAnisotropy);
            gl.texParameteri(gl.TEXTURE_2D, 
                gl.getExtension('EXT_texture_filter_anisotropic').TEXTURE_MAX_ANISOTROPY_EXT,
                anisotropy
            );
        }
    }

    /**
     * Generate mipmap levels for texture
     */
    generateMipmaps(texture, baseLevel = 0, maxLevel = null) {
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, texture);
        
        // Calculate max mipmap level if not provided
        if (maxLevel === null) {
            const size = gl.getTexParameter(gl.TEXTURE_2D, gl.TEXTURE_WIDTH) || 0;
            maxLevel = Math.floor(Math.log2(size));
        }
        
        // Set mipmap range
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, baseLevel);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, maxLevel);
        
        // Generate mipmaps
        gl.generateMipmap(gl.TEXTURE_2D);
        
        return {
            baseLevel,
            maxLevel,
            totalLevels: maxLevel - baseLevel + 1
        };
    }

    /**
     * Create texture atlas from multiple textures
     */
    async createAtlas(textures, options = {}) {
        return await this.atlasManager.createAtlas(textures, options);
    }

    /**
     * Stream texture with progressive loading
     */
    async streamTexture(url, options = {}) {
        return await this.streamingManager.streamTexture(url, options);
    }

    /**
     * Optimize GPU memory usage
     */
    optimizeMemory(aggressive = false) {
        return this.memoryManager.optimize(aggressive);
    }

    /**
     * Get memory usage statistics
     */
    getMemoryStats() {
        return this.memoryManager.getStats();
    }

    /**
     * Generate cache key for texture
     */
    _generateCacheKey(imageData, options) {
        return btoa(JSON.stringify({
            width: imageData.width || imageData.videoWidth,
            height: imageData.height || imageData.videoHeight,
            options
        }));
    }

    /**
     * Cleanup resources
     */
    dispose() {
        // Dispose textures
        for (const [key, textureData] of this.textureCache) {
            this.gl.deleteTexture(textureData.texture);
        }
        this.textureCache.clear();
        
        // Dispose atlas manager
        this.atlasManager.dispose();
        
        // Dispose streaming manager
        this.streamingManager.dispose();
        
        // Dispose memory manager
        this.memoryManager.dispose();
        
        // Terminate worker
        if (this.compressionWorker) {
            this.compressionWorker.terminate();
        }
    }
}

/**
 * TextureAtlasManager - Manages texture atlases for batching
 */
class TextureAtlasManager {
    constructor(gl) {
        this.gl = gl;
        this.atlases = new Map();
        this.nextAtlasId = 1;
    }

    async createAtlas(textures, options = {}) {
        const {
            maxWidth = 4096,
            maxHeight = 4096,
            padding = 2,
            method = 'shelf' // 'shelf' or 'bin'
        } = options;

        const atlasId = this.nextAtlasId++;
        
        // Calculate optimal atlas size
        const atlasInfo = this._calculateAtlasSize(textures, maxWidth, maxHeight, padding);
        
        // Create atlas canvas
        const canvas = document.createElement('canvas');
        canvas.width = atlasInfo.width;
        canvas.height = atlasInfo.height;
        const context = canvas.getContext('2d');
        
        // Layout textures
        const layout = this._layoutTextures(textures, atlasInfo, method);
        
        // Pack textures into atlas
        const mappings = {};
        for (const item of layout.items) {
            const { texture, x, y, width, height } = item;
            
            context.drawImage(texture, x, y);
            
            mappings[item.id] = {
                x: x / atlasInfo.width,
                y: y / atlasInfo.height,
                width: width / atlasInfo.width,
                height: height / atlasInfo.height,
                offsetX: x,
                offsetY: y,
                originalWidth: width,
                originalHeight: height
            };
        }
        
        // Create WebGL texture from atlas
        const atlasTexture = this.gl.createTexture();
        this.gl.bindTexture(this.gl.TEXTURE_2D, atlasTexture);
        this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, canvas);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR_MIPMAP_LINEAR);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
        this.gl.generateMipmap(this.gl.TEXTURE_2D);
        
        // Store atlas
        const atlas = {
            id: atlasId,
            texture: atlasTexture,
            canvas,
            context,
            mappings,
            textureCount: textures.length,
            usage: atlasInfo.usage,
            dimensions: { width: atlasInfo.width, height: atlasInfo.height }
        };
        
        this.atlases.set(atlasId, atlas);
        
        return {
            atlasId,
            atlas,
            mappings,
            dimensions: atlas.dimensions,
            usage: atlasInfo.usage
        };
    }

    _calculateAtlasSize(textures, maxWidth, maxHeight, padding) {
        let totalWidth = 0;
        let totalHeight = 0;
        
        for (const texture of textures) {
            totalWidth += texture.width + padding * 2;
            totalHeight = Math.max(totalHeight, texture.height + padding * 2);
        }
        
        // Find power of 2 sizes that fit within limits
        let width = 1;
        while (width < totalWidth && width < maxWidth) width *= 2;
        
        let height = 1;
        while (height < totalHeight && height < maxHeight) height *= 2;
        
        // Ensure we don't exceed maximums
        width = Math.min(width, maxWidth);
        height = Math.min(height, maxHeight);
        
        return {
            width,
            height,
            usage: (totalWidth * totalHeight) / (width * height)
        };
    }

    _layoutTextures(textures, atlasInfo, method) {
        if (method === 'shelf') {
            return this._shelfLayout(textures, atlasInfo);
        } else {
            return this._binLayout(textures, atlasInfo);
        }
    }

    _shelfLayout(textures, atlasInfo) {
        const items = [];
        let currentX = 0;
        let currentY = 0;
        let shelfHeight = 0;
        
        for (let i = 0; i < textures.length; i++) {
            const texture = textures[i];
            
            // Check if texture fits on current shelf
            if (currentX + texture.width > atlasInfo.width) {
                // Start new shelf
                currentX = 0;
                currentY += shelfHeight;
                shelfHeight = 0;
            }
            
            // Check if we need new shelf
            if (currentY + texture.height > atlasInfo.height) {
                console.warn('Atlas full, some textures may not fit');
                break;
            }
            
            items.push({
                id: i,
                texture,
                x: currentX,
                y: currentY,
                width: texture.width,
                height: texture.height
            });
            
            currentX += texture.width + 2;
            shelfHeight = Math.max(shelfHeight, texture.height);
        }
        
        return { items };
    }

    _binLayout(textures, atlasInfo) {
        // Simple bin packing - could be improved with more sophisticated algorithms
        const items = [];
        const bins = [{ x: 0, y: 0, width: atlasInfo.width, height: atlasInfo.height }];
        
        for (let i = 0; i < textures.length; i++) {
            const texture = textures[i];
            let placed = false;
            
            for (let j = 0; j < bins.length && !placed; j++) {
                const bin = bins[j];
                
                if (texture.width <= bin.width && texture.height <= bin.height) {
                    // Place texture in this bin
                    items.push({
                        id: i,
                        texture,
                        x: bin.x,
                        y: bin.y,
                        width: texture.width,
                        height: texture.height
                    });
                    
                    // Split bin
                    this._splitBin(bins, j, texture);
                    placed = true;
                }
            }
            
            if (!placed) {
                console.warn('Could not place texture in atlas');
            }
        }
        
        return { items };
    }

    _splitBin(bins, binIndex, texture) {
        const bin = bins[binIndex];
        
        // Split bin into right and bottom regions
        bins.splice(binIndex, 1);
        
        if (bin.width > texture.width) {
            bins.push({
                x: bin.x + texture.width,
                y: bin.y,
                width: bin.width - texture.width,
                height: texture.height
            });
        }
        
        if (bin.height > texture.height) {
            bins.push({
                x: bin.x,
                y: bin.y + texture.height,
                width: texture.width,
                height: bin.height - texture.height
            });
        }
    }

    getAtlas(atlasId) {
        return this.atlases.get(atlasId);
    }

    dispose() {
        for (const [id, atlas] of this.atlases) {
            this.gl.deleteTexture(atlas.texture);
        }
        this.atlases.clear();
    }
}

/**
 * TextureStreamingManager - Manages asynchronous texture loading
 */
class TextureStreamingManager {
    constructor(gl) {
        this.gl = gl;
        this.streamingTextures = new Map();
        this.loadingQueue = [];
        this.maxConcurrent = 3;
        this.activeLoads = 0;
    }

    async streamTexture(url, options = {}) {
        const {
            qualityLevels = ['low', 'medium', 'high'],
            progressive = true,
            onProgress = null,
            timeout = 30000
        } = options;

        return new Promise((resolve, reject) => {
            const textureId = Date.now() + Math.random();
            const request = {
                id: textureId,
                url,
                qualityLevels,
                progressive,
                onProgress,
                timeout,
                resolve,
                reject,
                startTime: Date.now(),
                currentLevel: 0
            };

            this._processStreamingRequest(request);
        });
    }

    async _processStreamingRequest(request) {
        try {
            const texture = this.gl.createTexture();
            this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
            
            // Set temporary texture
            this._setTemporaryTexture(texture);
            
            // Stream quality levels
            for (let i = 0; i < request.qualityLevels.length; i++) {
                const level = request.qualityLevels[i];
                
                try {
                    const imageData = await this._loadImageLevel(request.url, level, request.timeout);
                    
                    // Update texture progressively
                    await this._updateTexture(texture, imageData, level);
                    
                    if (request.onProgress) {
                        request.onProgress((i + 1) / request.qualityLevels.length, level);
                    }
                    
                    request.currentLevel = i;
                    
                } catch (error) {
                    console.warn(`Failed to load ${level} quality for ${request.url}:`, error);
                    
                    if (i === 0) {
                        // If even the lowest quality fails, reject
                        throw error;
                    }
                    // Otherwise, continue with what we have
                }
            }
            
            this.gl.bindTexture(this.gl.TEXTURE_2D, null);
            
            resolve({
                texture,
                finalQuality: request.qualityLevels[request.currentLevel] || 'low',
                loadTime: Date.now() - request.startTime,
                qualityLevels: request.currentLevel + 1
            });
            
        } catch (error) {
            request.reject(error);
        }
    }

    _setTemporaryTexture(texture) {
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, texture);
        
        // Create 1x1 blue texture as placeholder
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'blue';
        ctx.fillRect(0, 0, 1, 1);
        
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    }

    async _loadImageLevel(url, quality, timeout) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const timer = setTimeout(() => {
                img.src = '';
                reject(new Error(`Timeout loading ${quality} quality texture`));
            }, timeout);

            img.onload = () => {
                clearTimeout(timer);
                resolve(img);
            };

            img.onerror = () => {
                clearTimeout(timer);
                reject(new Error(`Failed to load ${quality} quality texture`));
            };

            // Add quality parameter to URL
            const qualityUrl = this._addQualityParameter(url, quality);
            img.src = qualityUrl;
        });
    }

    _addQualityParameter(url, quality) {
        // Add quality parameter to URL (depends on server implementation)
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}quality=${quality}`;
    }

    async _updateTexture(texture, imageData, quality) {
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, texture);
        
        if (quality === 'low') {
            // Use NEAREST filtering for low quality
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        } else {
            // Use LINEAR filtering for higher quality
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        }
        
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imageData);
    }

    dispose() {
        this.streamingTextures.clear();
        this.loadingQueue = [];
    }
}

/**
 * GPUMemoryManager - Optimizes GPU memory usage
 */
class GPUMemoryManager {
    constructor(gl) {
        this.gl = gl;
        this.textures = new Map();
        this.memoryBudget = this._calculateMemoryBudget();
        this.currentUsage = 0;
        this.compressionThreshold = 0.8; // 80% of memory budget
    }

    _calculateMemoryBudget() {
        // Estimate available GPU memory (simplified)
        const canvas = this.gl.canvas;
        const memoryInfo = this.gl.getExtension('WEBGL_debug_renderer_info') ? 
            this.gl.getExtension('WEBGL_debug_renderer_info').UNMASKED_RENDERER_WEBGL : null;
        
        // Default to 512MB budget if can't determine
        let budget = 512 * 1024 * 1024; // 512MB
        
        // Adjust based on device type
        if (/Mobile|Android|iPhone|iPad/.test(navigator.userAgent)) {
            budget = 256 * 1024 * 1024; // 256MB for mobile
        }
        
        return budget;
    }

    registerTexture(id, texture, info) {
        const memoryUsage = this._estimateTextureMemory(info);
        
        this.textures.set(id, {
            texture,
            info,
            memoryUsage,
            lastAccessed: Date.now(),
            accessCount: 0
        });
        
        this.currentUsage += memoryUsage;
    }

    _estimateTextureMemory(info) {
        const { width, height, channels = 4, compressed = false, mipmaps = 1 } = info;
        
        if (compressed) {
            // Compressed textures use less memory
            const blockSize = channels === 4 ? 16 : 8; // 4x4 blocks for RGBA, 4x4 for RGB
            const blocksX = Math.ceil(width / 4);
            const blocksY = Math.ceil(height / 4);
            return blocksX * blocksY * blockSize * mipmaps;
        } else {
            // Uncompressed textures
            return width * height * channels * 4 * mipmaps; // 4 bytes per pixel
        }
    }

    optimize(aggressive = false) {
        let freed = 0;
        
        // Sort textures by usage (least recently used first)
        const sortedTextures = Array.from(this.textures.entries())
            .sort(([,a], [,b]) => a.lastAccessed - b.lastAccessed);
        
        for (const [id, textureData] of sortedTextures) {
            if (this.currentUsage <= this.memoryBudget * (aggressive ? 0.5 : 0.7)) {
                break;
            }
            
            // Don't delete frequently used textures
            if (textureData.accessCount > 10 && !aggressive) {
                continue;
            }
            
            // Compress or delete texture
            if (textureData.info.compressed) {
                // Texture is already compressed
                continue;
            }
            
            // Delete texture to free memory
            this.gl.deleteTexture(textureData.texture);
            this.textures.delete(id);
            this.currentUsage -= textureData.memoryUsage;
            freed += textureData.memoryUsage;
        }
        
        return {
            freed,
            currentUsage: this.currentUsage,
            memoryBudget: this.memoryBudget,
            usagePercentage: (this.currentUsage / this.memoryBudget) * 100
        };
    }

    getStats() {
        const compressedCount = Array.from(this.textures.values())
            .filter(t => t.info.compressed).length;
        
        return {
            totalTextures: this.textures.size,
            compressedTextures: compressedCount,
            uncompressedTextures: this.textures.size - compressedCount,
            currentUsage: this.currentUsage,
            memoryBudget: this.memoryBudget,
            usagePercentage: (this.currentUsage / this.memoryBudget) * 100,
            textures: Array.from(this.textures.entries()).map(([id, data]) => ({
                id,
                memoryUsage: data.memoryUsage,
                compressed: data.info.compressed,
                accessCount: data.accessCount,
                lastAccessed: data.lastAccessed
            }))
        };
    }

    accessTexture(id) {
        const texture = this.textures.get(id);
        if (texture) {
            texture.lastAccessed = Date.now();
            texture.accessCount++;
        }
        return texture;
    }

    dispose() {
        for (const [id, textureData] of this.textures) {
            this.gl.deleteTexture(textureData.texture);
        }
        this.textures.clear();
        this.currentUsage = 0;
    }
}

// Export using ES6 modules
export { TextureCompression };
